import { Component, OnInit, Output, EventEmitter, Input, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { SelectedDateRange } from "./../../models/selected-date-range";
import { DiaryCalendarView } from "./../../models/diary-calendar-view";
import { DiaryEventGroup } from "../../models/diary-event-group"

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'mojo-calendar',
  templateUrl: './calendar.component.html',
  styleUrls: ['./calendar.component.scss']
})
export class CalendarComponent implements OnInit {
  @Output() onDateChanged = new EventEmitter<SelectedDateRange>();
  @Input() date: Date = new Date();
  @Input() view: DiaryCalendarView = DiaryCalendarView.Month;
  @Output() viewChange = new EventEmitter<DiaryCalendarView>();

  private _diaryEvents: DiaryEventGroup[];

  @Input() set diaryEvents(value: DiaryEventGroup[]) {
    this._diaryEvents = value;
  }

  get diaryEvents(): DiaryEventGroup[] {
    return this._diaryEvents;
  } 


  fromYear: number = 1980;
  toYear: number = 2208;
  
  constructor(private changeDetector: ChangeDetectorRef) {
  }

  ngOnInit() {
    this.generateDateRangeAndEmit();
  }

  detectChanges() {
    this.changeDetector.detectChanges();
    this.changeDetector.markForCheck();
  }

  generateDateRangeAndEmit() {
    var selectedDateRange: SelectedDateRange = new SelectedDateRange();
    selectedDateRange.View = this.view;
    selectedDateRange.SelectedDate = this.date;

    var startYear: number = this.date.getFullYear();
    var startMonth: number = this.date.getMonth();
    var startDay: number = this.date.getDate();
    var endYear: number = startYear;
    var endMonth: number = startMonth;
    var endDay: number = startDay;

    switch (this.view) {
      case DiaryCalendarView.Duodecennial:
        {
          startDay = 1;
          startMonth = 0;
          endDay = 1;
          endMonth = 0;
          startYear = Math.floor(startYear / 12) * 12;
          endYear = startYear + 11;

          break;
        }
      case DiaryCalendarView.Year:
        {
          startDay = 1;
          startMonth = 0;
          endDay = 1;
          endMonth = 0;
          endYear = startYear + 1;
          break;
        }
      case DiaryCalendarView.Month:
        {
          startDay = 1;
          endDay = 1;
          endMonth = startMonth + 1;
          if (endMonth > 11) {
            endMonth = 0;
            endYear = startYear + 1;
          }
          break;
        }
      case DiaryCalendarView.Day:
      {
          selectedDateRange.SelectedDate.setFullYear(startYear, startMonth, startDay);
          startDay = 1;
          endDay = 1;
          
          
          endMonth = startMonth + 1;
          if (endMonth > 11) {
            endMonth = 0;
            endYear = startYear + 1;
          }
          break;
        }
      default:
    }

    selectedDateRange.StartDate = new Date('2000/01/01');
    selectedDateRange.StartDate.setFullYear(startYear, startMonth, startDay);
    selectedDateRange.EndDate = new Date('2000/01/010');
    selectedDateRange.EndDate.setFullYear(endYear, endMonth, endDay);
    selectedDateRange.EndDate.setHours(-2);

    this.onDateChanged.emit(selectedDateRange);
  }

  getDaysInMonth(year: number, month: number): number {
    var daysInMonth: number = new Date(year, month + 1, 0).getDate();
    return daysInMonth;
  }

  navigateBy(amount) {
    switch (this.view) {
      case DiaryCalendarView.Duodecennial:
        {
          let newYear = this.date.getUTCFullYear() + (amount * 12);
          if (newYear >= this.fromYear && newYear < this.toYear) {
            this.date = new Date(this.date.setUTCFullYear(newYear));
            break;
          }
          return;
        }
      case DiaryCalendarView.Year:
        {
          let newYear = this.date.getUTCFullYear() + amount;
          if (newYear >= this.fromYear && newYear < this.toYear) {
            this.date = new Date(this.date.setUTCFullYear(this.date.getUTCFullYear() + amount));
            break;
          }
          return;
        }
      case DiaryCalendarView.Month:
        {
          let newDate = new Date(this.date);
          newDate.setUTCMonth(this.date.getUTCMonth() + amount);
          if (newDate.getUTCFullYear() >= this.fromYear && newDate.getUTCFullYear() < this.toYear) {
            this.date = new Date(this.date.setUTCMonth(this.date.getUTCMonth() + amount));
            break;
          }
          return;
        }
      case DiaryCalendarView.Day:
        {
          let newDate = new Date(this.date);
          newDate.setUTCDate(this.date.getUTCDate() + amount);
          if (newDate.getUTCFullYear() >= this.fromYear && newDate.getUTCFullYear() < this.toYear) {
            this.date = newDate;
            break;
          }
          return;
        }
      default:
    }
    this.generateDateRangeAndEmit();
    this.detectChanges();
  }

  navigateBarClicked() {
    switch (this.view) {
      case DiaryCalendarView.Duodecennial:
        {
          // nothing to do as we are at the most generic
          break;
        }
      case DiaryCalendarView.Year:
        {
          this.view = DiaryCalendarView.Duodecennial;
          this.viewChange.emit(this.view);
          break;
        }
      case DiaryCalendarView.Month:
        {
          this.view = DiaryCalendarView.Year;
          this.viewChange.emit(this.view);
          break;
        }
      case DiaryCalendarView.Day:
        {
          this.view = DiaryCalendarView.Month;
          this.viewChange.emit(this.view);
          break;
        }
      default:
    }
    this.generateDateRangeAndEmit();
    this.detectChanges();
  }

  yearClicked(year) {
    this.date = new Date('01/01/' + year);
    this.view = DiaryCalendarView.Year;
    this.viewChange.emit(this.view);
    this.detectChanges();
    this.navigateBy(0);
  }

  monthClicked(month) {
    this.date = new Date(month + '/01/' + this.date.getUTCFullYear());
    this.view = DiaryCalendarView.Month;
    this.viewChange.emit(this.view);
    this.detectChanges();
    this.navigateBy(0);
  }

  dayClicked(date: Date) {
    this.date = date;
    this.view = DiaryCalendarView.Day;
    this.viewChange.emit(this.view);
    this.detectChanges();
    this.navigateBy(0);
  }

}
